import { Routes, Route, Navigate } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import type { JSX } from 'react';
import { LoginPage } from './features/auth/LoginPage';
import { AppShell } from './layouts/AppShell';
import { useAuthStore } from './auth/store';
import { useG6Sso } from './features/auth/mutations';
import SampleBacklogPage from './features/backlog/SampleBacklogPage';
import SamplingAllocationPage from './features/sampling/SamplingAllocationPage';
import DashboardFramePage from './features/dashboards/DashboardFramePage';
import { useEnsureThemeApplied } from './theme/useAppTheme';

/** Route guard — unauthenticated users are sent to /login (CLAUDE.md 7.x). */
function ProtectedRoute({ children }: { children: JSX.Element }) {
  const token = useAuthStore((s) => s.token);
  return token ? children : <Navigate to="/login" replace />;
}

/**
 * G6 SSO gate — when ALPACA LIMS opens this app with `?tkn=` (the G6 header
 * button), exchange the one-time token for a session BEFORE any route
 * renders, so a stale session in this browser (e.g. "service") can never
 * flash or win over the LIMS user. The token is stripped from the URL
 * immediately — it must not survive in history/bookmarks. On failure the
 * mutation drops any existing session and the normal login page shows.
 */
function G6SsoGate({ children }: { children: JSX.Element }) {
  const sso = useG6Sso();
  // Capture + strip the token synchronously on first render.
  const [tkn] = useState<string | null>(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('tkn');
    if (t) {
      params.delete('tkn');
      const rest = params.toString();
      window.history.replaceState(
        null, '',
        window.location.pathname + (rest ? `?${rest}` : '') + window.location.hash);
    }
    return t;
  });
  const fired = useRef(false);
  useEffect(() => {
    if (tkn && !fired.current) {
      fired.current = true;
      sso.mutate({ tkn });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tkn]);

  if (tkn && !sso.isSuccess && !sso.isError) {
    return (
      <div style={{
        height: '100vh', display: 'flex', alignItems: 'center',
        justifyContent: 'center', flexDirection: 'column', gap: 12,
        fontFamily: 'inherit', color: 'var(--text-primary, #333)',
      }}>
        <div>Signing you in from ALPACA LIMS…</div>
      </div>
    );
  }
  return children;
}

export function App() {
  // Propagate the chosen Login theme to every form's chrome (popup title
  // bars, table headers, hover colours).  Reads localStorage once on mount
  // and writes CSS custom properties to :root so consumers can use either
  // useAppTheme() (live re-render) or var(--brand-accent) directly.
  useEnsureThemeApplied();

  return (
    <G6SsoGate>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        {/* Deep-link per form so each browser tab can sit on its own form
            (refresh-safe, duplicable). */}
        <Route path="/m/:moduleKey" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        {/* Same shell, with a record id — lets a module's own detail page (e.g.
            ICM Operations' Requisition/Purchase Order full-page New/Edit) render
            inside AppShell instead of as an orphaned standalone route (no sidebar/
            top bar). AppShell reads the id via useParams and swaps in the detail
            page for the modules that use one. */}
        <Route path="/m/:moduleKey/:id" element={<ProtectedRoute><AppShell /></ProtectedRoute>} />
        {/* Sample Backlog — standalone full-height page */}
        <Route path="/backlog" element={<ProtectedRoute><SampleBacklogPage /></ProtectedRoute>} />
        {/* Sampling Allocation — Sample Scheduling module */}
        <Route path="/sampling-allocation" element={<ProtectedRoute><SamplingAllocationPage /></ProtectedRoute>} />
        {/* Standalone dashboard FRAME (new tab from "Show Dashboard in a separate tab")
            — the dashboard + its Design/View/Back toolbar, no app shell. */}
        <Route path="/dashboard-frame/:navId" element={<ProtectedRoute><DashboardFramePage /></ProtectedRoute>} />
        {/* Design-review preview — shell without auth (no token minted). */}
        <Route path="/preview" element={<AppShell />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </G6SsoGate>
  );
}
